/**
 * ProfitSpot — Marketing Landing Page
 * Professional DeFi yield intelligence platform
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Shield, Zap, Users, BarChart3, Bell,
  Check, ChevronRight, ArrowRight,
  Star, Lock, Crown, Sparkles, Globe, Clock, DollarSign,
  AlertTriangle, Target, LineChart, Wallet, ExternalLink,
  Brain, Search, BookOpen, TrendingDown, CheckCircle, Menu, X,
  Heart, Rocket
} from 'lucide-react';
import LegalDisclaimerModal from '../components/LegalDisclaimerModal';

// ─── Animated Counter ─────────────────────────────────────────
const AnimatedNumber = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration]);
  return <span>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// ─── Feature Card ─────────────────────────────────────────────
const FeatureCard = ({ icon: Icon, title, description, accent = '#00ff88', badge }) => (
  <div className="group bg-[#0f0f1a]/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all duration-300 hover:transform hover:scale-[1.02] relative overflow-hidden">
    {badge && (
      <span className="absolute top-4 right-4 text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ color: accent, backgroundColor: accent + '20' }}>
        {badge}
      </span>
    )}
    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${accent}15` }}>
      <Icon className="w-6 h-6" style={{ color: accent }} />
    </div>
    <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
    <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
  </div>
);

// ─── Pricing Tier ─────────────────────────────────────────────
const PricingTier = ({ name, price, period, features, accent, popular, cta, onCTA, finePrint }) => (
  <div className={`relative bg-[#0f0f1a] rounded-2xl p-6 border ${popular ? 'border-[#00ff88]/50 scale-105' : 'border-gray-800'} transition-all hover:border-gray-700 flex flex-col`}>
    {popular && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#00ff88] text-black text-xs font-bold rounded-full whitespace-nowrap">
        MOST POPULAR
      </div>
    )}
    <div className="text-center mb-6">
      <h3 className="text-lg font-bold text-white mb-1">{name}</h3>
      <div className="flex items-baseline justify-center gap-1">
        <span className="text-4xl font-bold" style={{ color: accent }}>${price}</span>
        {period && <span className="text-gray-500 text-sm">/{period}</span>}
      </div>
    </div>
    <ul className="space-y-3 mb-6 flex-1">
      {features.map((feature, i) => (
        <li key={i} className="flex items-start gap-3 text-sm">
          <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accent }} />
          <span className="text-gray-300">{feature}</span>
        </li>
      ))}
    </ul>
    <button
      onClick={onCTA}
      className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
        popular ? 'bg-[#00ff88] text-black hover:bg-[#00cc6a]' : 'bg-gray-800 text-white hover:bg-gray-700'
      }`}
    >
      {cta}
    </button>
    {finePrint && <p className="text-[10px] text-gray-600 text-center mt-2 leading-relaxed">{finePrint}</p>}
  </div>
);

// ─── Time to Profit Chart ─────────────────────────────────────
const TimeToProfitChart = () => {
  const W = 280, H = 85, pad = { t: 12, r: 8, b: 18, l: 28 };
  const cW = W - pad.l - pad.r, cH = H - pad.t - pad.b;
  const DAYS = 60, APY = 1.9988, DEPOSIT = 1000, COSTS = 100.04;
  const daily = DEPOSIT * (APY / 365);
  const breakDay = COSTS / daily;
  const xS = (d) => pad.l + (d / DAYS) * cW;
  const yMax = DAYS * daily;
  const yS = (v) => pad.t + cH - Math.min((v / yMax) * cH, cH);
  const pts = Array.from({ length: 61 }, (_, d) => ({ d, y: d * daily }));
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${xS(p.d).toFixed(1)},${yS(p.y).toFixed(1)}`).join(' ');
  const area = `${line} L${xS(DAYS).toFixed(1)},${pad.t + cH} L${pad.l},${pad.t + cH}Z`;
  const beY = yS(COSTS), beX = xS(breakDay);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: '85px' }}>
      <defs>
        <linearGradient id="lp-ttp" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00ff88" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#00ff88" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <line x1={pad.l} y1={beY.toFixed(1)} x2={pad.l + cW} y2={beY.toFixed(1)} stroke="#00d4ff" strokeWidth="0.8" strokeDasharray="3,2" opacity="0.65" />
      <text x={pad.l + 3} y={parseFloat(beY.toFixed(1)) - 2} fontSize="6" fill="#00d4ff" opacity="0.7">break-even</text>
      <path d={area} fill="url(#lp-ttp)" />
      <path d={line} fill="none" stroke="#00ff88" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <line x1={beX.toFixed(1)} y1={pad.t} x2={beX.toFixed(1)} y2={pad.t + cH} stroke="#ffa502" strokeWidth="0.8" strokeDasharray="2,2" opacity="0.8" />
      <circle cx={beX.toFixed(1)} cy={beY.toFixed(1)} r="3.5" fill="#ffa502" stroke="#0a0a12" strokeWidth="1.5" />
      <text x={pad.l} y={H - 2} fontSize="6" fill="#4b5563">Day 1</text>
      <text x={xS(30).toFixed(1)} y={H - 2} fontSize="6" fill="#4b5563" textAnchor="middle">30d</text>
      <text x={pad.l + cW} y={H - 2} textAnchor="end" fontSize="6" fill="#4b5563">60d</text>
      <text x={pad.l - 3} y={pad.t + 3} textAnchor="end" fontSize="6" fill="#4b5563">${Math.round(yMax / 1000)}k</text>
      <text x={pad.l - 3} y={pad.t + cH} textAnchor="end" fontSize="6" fill="#4b5563">$0</text>
    </svg>
  );
};

// ─── Enhanced Pool Row ─────────────────────────────────────────
const EnhancedPoolRow = ({ symbol, protocol, chain, grossApy, netApy, breakEven, rating }) => (
  <div className="p-2.5 bg-[#050508] rounded-lg border border-gray-800/50 hover:border-gray-700/50 transition-colors">
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#00ff88]/20 to-[#00d4ff]/20 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
          {symbol[0]}
        </div>
        <div>
          <p className="text-xs font-bold text-white leading-tight">{symbol}</p>
          <p className="text-[10px] text-gray-500">{protocol} · {chain}</p>
        </div>
      </div>
      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${rating === 'TOP' ? 'bg-[#00ff88]/15 text-[#00ff88]' : rating === 'SOLID' ? 'bg-[#00d4ff]/15 text-[#00d4ff]' : 'bg-[#ffa502]/15 text-[#ffa502]'}`}>
        {rating}
      </span>
    </div>
    <div className="grid grid-cols-3 gap-1 text-center">
      <div><p className="text-[9px] text-gray-600">Gross APY</p><p className="text-xs font-bold text-[#00d4ff]">{grossApy}%</p></div>
      <div><p className="text-[9px] text-gray-600">Net APY</p><p className="text-xs font-bold text-[#00ff88]">{netApy}%</p></div>
      <div><p className="text-[9px] text-gray-600">Break-even</p><p className="text-xs font-bold text-white">{breakEven}</p></div>
    </div>
  </div>
);

// ─── Alex's Story Component ────────────────────────────────────
const AlexStory = ({ onCTA }) => (
  <section className="py-24 px-4" id="story">
    <div className="max-w-6xl mx-auto">

      {/* Header */}
      <div className="text-center mb-14">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00d4ff]/10 border border-[#00d4ff]/20 rounded-full mb-5">
          <BookOpen className="w-4 h-4 text-[#00d4ff]" />
          <span className="text-sm text-[#00d4ff] font-medium">Hypothetical Scenario — For Illustration Only</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold mb-4">
          How ProfitSpot <span className="text-[#00ff88]">Saved Alex $4,200</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto text-sm leading-relaxed">
          This is a hypothetical scenario illustrating risks that many LP investors face —
          not because they're careless, but because the right data simply wasn't in front of them.
          <em className="block mt-1 text-gray-500 text-xs">"Alex" is a composite, illustrative persona, not a real user.</em>
        </p>
      </div>

      {/* Side-by-Side Comparison */}
      <div className="grid lg:grid-cols-2 gap-6 mb-10">

        {/* ── LEFT: Without ProfitSpot ── */}
        <div className="bg-[#0f0f1a] border border-red-500/25 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 bg-red-500/10 border-b border-red-500/20 flex items-center justify-between">
            <span className="text-sm font-bold text-red-400">Without ProfitSpot</span>
            <div className="flex items-center gap-1.5">
              <TrendingDown className="w-4 h-4 text-red-400" />
              <span className="text-xs text-red-400 font-bold">-$2,400</span>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {[
              { n: '01', text: 'Spots a 120% APY pool on a new DEX. No security check, no IL estimate, no true net APY data. Deposits $10,000 on gut feel.' },
              { n: '02', text: "The pool's governance token crashes 80% in 90 days. Impermanent loss silently destroys 25% of the position. Real yield: 4% — not even close." },
              { n: '03', text: <span>Withdraws 90 days later: <span className="text-red-400 font-bold text-base">$7,600.</span> Lost $2,400. The 120% APY lasted 2 weeks.</span> },
            ].map(({ n, text }) => (
              <div key={n} className="flex items-start gap-3 p-3 bg-[#0a0a12] rounded-xl border border-red-500/10">
                <span className="text-[10px] font-bold text-red-500/50 mt-0.5 w-4 flex-shrink-0">{n}</span>
                <p className="text-sm text-gray-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          {/* Data breakdown */}
          <div className="mx-5 mb-5 grid grid-cols-2 gap-2">
            {[
              { label: 'Advertised APY', val: '120%', color: 'text-gray-500' },
              { label: 'True Net APY', val: '4%', color: 'text-[#ffa502]' },
              { label: 'Security Score', val: '12/100', color: 'text-red-400' },
              { label: 'IL Loss', val: '-$2,500', color: 'text-red-400 font-bold' },
            ].map(({ label, val, color }) => (
              <div key={label} className="bg-[#0a0a12] rounded-lg p-2.5 text-center border border-red-500/10">
                <p className="text-[10px] text-gray-600 mb-1">{label}</p>
                <p className={`text-sm font-bold ${color}`}>{val}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: With ProfitSpot ── */}
        <div className="bg-[#0f0f1a] border border-[#00ff88]/25 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 bg-[#00ff88]/10 border-b border-[#00ff88]/20 flex items-center justify-between">
            <span className="text-sm font-bold text-[#00ff88]">With ProfitSpot</span>
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4 text-[#00ff88]" />
              <span className="text-xs text-[#00ff88] font-bold">+$1,800</span>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {[
              { n: '01', text: <span>ProfitSpot flags the same pool instantly: <span className="text-[#ffa502] font-semibold">Security Score 12/100</span>, True Net APY 4%, IL Risk HIGH. Trap spotted in 30 seconds.</span> },
              { n: '02', text: <span>Yield Explorer surfaces a better match: <span className="text-white font-semibold">ETH-USDC on Arbitrum</span> — 44% Net APY, Security Score 96/100, IL Risk LOW. Deposits $10,000 with full data.</span> },
              { n: '03', text: <span>Withdraws 90 days later: <span className="text-[#00ff88] font-bold text-base">$11,800.</span> Gained $1,800. Protected and growing.</span> },
            ].map(({ n, text }) => (
              <div key={n} className="flex items-start gap-3 p-3 bg-[#0a0a12] rounded-xl border border-[#00ff88]/10">
                <span className="text-[10px] font-bold text-[#00ff88]/40 mt-0.5 w-4 flex-shrink-0">{n}</span>
                <p className="text-sm text-gray-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          {/* Data breakdown */}
          <div className="mx-5 mb-5 grid grid-cols-2 gap-2">
            {[
              { label: 'True Net APY', val: '44%', color: 'text-[#00d4ff]' },
              { label: 'Security Score', val: '96/100', color: 'text-[#00ff88]' },
              { label: 'IL Risk', val: 'LOW', color: 'text-[#00ff88]' },
              { label: '90-Day Gain', val: '+$1,800', color: 'text-[#00ff88] font-bold' },
            ].map(({ label, val, color }) => (
              <div key={label} className="bg-[#0a0a12] rounded-lg p-2.5 text-center border border-[#00ff88]/10">
                <p className="text-[10px] text-gray-600 mb-1">{label}</p>
                <p className={`text-sm font-bold ${color}`}>{val}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Outcome Bar */}
      <div className="bg-[#0f0f1a] border border-gray-800 rounded-2xl p-6">
        <div className="grid sm:grid-cols-3 gap-6 items-center">
          {/* Without */}
          <div className="text-center sm:text-left">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Without ProfitSpot</p>
            <p className="text-3xl font-bold text-red-400">$7,600</p>
            <p className="text-sm text-red-400/70 mt-0.5">-$2,400 · -24% loss</p>
          </div>

          {/* Difference (center) */}
          <div className="text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Total Difference</p>
            <p className="text-4xl font-bold text-[#00ff88]">$4,200</p>
            <p className="text-sm text-gray-500 mt-1">on the same $10,000</p>
            <button onClick={onCTA}
              className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 bg-[#00ff88] text-black text-sm font-bold rounded-xl hover:bg-[#00cc6a] transition-all">
              Start Like Alex <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* With */}
          <div className="text-center sm:text-right">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">With ProfitSpot</p>
            <p className="text-3xl font-bold text-[#00ff88]">$11,800</p>
            <p className="text-sm text-[#00ff88]/70 mt-0.5">+$1,800 · +18% gain</p>
          </div>
        </div>

        {/* Progress bar visualization */}
        <div className="mt-5 pt-5 border-t border-gray-800">
          <div className="flex items-center gap-3">
            <span className="text-xs text-red-400 w-14 text-right flex-shrink-0">$7,600</span>
            <div className="flex-1 h-3 bg-gray-900 rounded-full overflow-hidden flex">
              <div className="h-full bg-red-500/50 rounded-l-full transition-all" style={{ width: '39%' }} />
              <div className="h-full bg-[#00ff88]/70 rounded-r-full transition-all" style={{ width: '61%' }} />
            </div>
            <span className="text-xs text-[#00ff88] w-14 flex-shrink-0">$11,800</span>
          </div>
          <div className="flex justify-center mt-2">
            <p className="text-[10px] text-gray-600">Same investor · Same $10,000 · Same 90 days · Different data</p>
          </div>
        </div>
      </div>

    </div>
  </section>
);

// ─── Portfolio Preview Card ────────────────────────────────────
// ─── Portfolio Preview with AI "Should I Exit?" ─────────────────
const PortfolioPreview = () => {
  const [showAI, setShowAI] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDone, setAiDone] = useState(false);

  const handleExitAnalysis = () => {
    if (showAI) return;
    setShowAI(true);
    setAiLoading(true);
    setTimeout(() => { setAiLoading(false); setAiDone(true); }, 1600);
  };

  return (
    <div className="bg-[#0a0a12] border border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 transition-colors">
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
          </div>
          <span className="text-xs text-gray-500 ml-1">Portfolio Tracker — 0xd8dA...6045</span>
        </div>
        <span className="text-[10px] text-[#00d4ff]">Live Moralis Data</span>
      </div>
      <div className="p-4 space-y-2">
        {/* Position rows */}
        {[
          { pair: 'WETH / USDC', protocol: 'Uniswap V3', il: 'Medium', ilColor: '#ffa502', val: '$8,420', highlight: true },
          { pair: 'USDC (Supplied)', protocol: 'Aave V3', il: 'None', ilColor: '#00ff88', val: '$5,000' },
          { pair: '3pool Stable', protocol: 'Curve Finance', il: 'Minimal', ilColor: '#00ff88', val: '$3,200' },
        ].map((pos, i) => (
          <div key={i} className={`p-2.5 rounded-lg border ${pos.highlight ? 'bg-[#0f0f1a] border-[#ffa502]/20' : 'bg-[#050508] border-gray-800/50'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-white">{pos.pair}</p>
                <p className="text-[10px] text-gray-500">{pos.protocol}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: pos.ilColor, backgroundColor: pos.ilColor + '18' }}>{pos.il}</span>
                <span className="text-xs font-bold text-white">{pos.val}</span>
                {pos.highlight && (
                  <button
                    onClick={handleExitAnalysis}
                    className="text-[10px] font-bold px-2 py-1 rounded-lg bg-[#00d4ff]/10 border border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/20 transition-colors whitespace-nowrap"
                  >
                    Should I Exit?
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* AI Response Panel */}
        {showAI && (
          <div className="mt-1 bg-[#050510] border border-[#00d4ff]/20 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-3.5 h-3.5 text-[#00d4ff]" />
              <span className="text-[10px] font-bold text-[#00d4ff] uppercase tracking-wider">AI Data Bot — WETH/USDC Analysis</span>
              {aiLoading && <div className="flex gap-0.5 ml-1">{[0,1,2].map(i=><div key={i} className="w-1 h-1 rounded-full bg-[#00d4ff] animate-bounce" style={{animationDelay:`${i*0.15}s`}}/>)}</div>}
            </div>
            {aiLoading && (
              <p className="text-[10px] text-gray-500 animate-pulse">Analyzing IL exposure, APY trajectory, gas costs, price divergence...</p>
            )}
            {aiDone && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#ffa502]/15 text-[#ffa502] border border-[#ffa502]/30">PROCEED WITH CAUTION</span>
                  <span className="text-[10px] text-gray-500">Confidence: 81%</span>
                </div>
                <div className="space-y-1 text-[10px] text-gray-400">
                  <p className="flex items-start gap-1.5"><span className="text-[#ffa502] flex-shrink-0">•</span>ETH price diverged 14% from entry — IL currently eroding ~$340 of principal</p>
                  <p className="flex items-start gap-1.5"><span className="text-[#00ff88] flex-shrink-0">•</span>APY (42.8%) still outpacing IL accumulation rate — position net positive at current pace</p>
                  <p className="flex items-start gap-1.5"><span className="text-[#00d4ff] flex-shrink-0">•</span>Break-even recouped at day 9 — every day held adds ~$12 net after costs</p>
                </div>
                <div className="border-t border-gray-800 pt-2">
                  <p className="text-[10px] text-white font-medium">Recommendation: <span className="text-[#ffa502]">Hold 15 more days</span>, then reassess. Set an alert at 20% ETH divergence to protect principal.</p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-1 border-t border-gray-800">
          <span className="text-[10px] text-gray-500">Total Portfolio Value</span>
          <span className="text-xs font-bold text-white">$16,620</span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Landing Page ─────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Always show disclaimer before entering the app — no sessionStorage skip
  const handleGetStarted = () => {
    setShowDisclaimer(true);
  };

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white overflow-x-hidden">
      {/* Auto-pops on first visit; also controlled via showDisclaimer */}
      <LegalDisclaimerModal
        forceOpen={showDisclaimer}
        onAccepted={() => { setShowDisclaimer(false); navigate('/explore'); }}
      />
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a12]/80 backdrop-blur-xl border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00ff88] to-[#00d4ff] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-black" />
              </div>
              <span className="text-xl font-bold">PROFIT<span className="text-[#00ff88]">SPOT</span></span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-gray-400 hover:text-white transition-colors">Features</a>
              <a href="#story" className="text-sm text-gray-400 hover:text-white transition-colors">Real Story</a>
              <a href="#how-it-works" className="text-sm text-gray-400 hover:text-white transition-colors">How It Works</a>
              <a href="#pricing" className="text-sm text-gray-400 hover:text-white transition-colors">Pricing</a>
              <a href="#founder" className="text-sm text-gray-400 hover:text-white transition-colors">Founder</a>
            </div>
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div className="flex items-center gap-3">
              <button onClick={handleGetStarted} className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors">
                Log In
              </button>
              <button onClick={handleGetStarted} className="px-4 py-2 bg-[#00ff88] text-black text-sm font-bold rounded-lg hover:bg-[#00cc6a] transition-colors">
                Get Started Free
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile nav menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0a0a12]/95 backdrop-blur-xl border-b border-gray-800/50 px-4 pb-4">
          <div className="flex flex-col gap-3 pt-2">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-400 hover:text-white transition-colors py-2 border-b border-gray-800/50">Features</a>
            <a href="#story" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-400 hover:text-white transition-colors py-2 border-b border-gray-800/50">Real Story</a>
            <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-400 hover:text-white transition-colors py-2 border-b border-gray-800/50">How It Works</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-400 hover:text-white transition-colors py-2 border-b border-gray-800/50">Pricing</a>
            <a href="#founder" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-400 hover:text-white transition-colors py-2 border-b border-gray-800/50">Founder</a>
            <button onClick={() => { setMobileMenuOpen(false); handleGetStarted(); }} className="mt-1 w-full px-4 py-3 bg-[#00ff88] text-black text-sm font-bold rounded-lg hover:bg-[#00cc6a] transition-colors">
              Get Started Free
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative pt-24 pb-14 px-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#00ff88]/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00d4ff]/10 rounded-full blur-[100px]" />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />

        <div className="relative max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00ff88]/10 border border-[#00ff88]/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-[#00ff88]" />
            <span className="text-sm text-[#00ff88] font-medium">Non-Custodial · Your Keys, Your Crypto</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-5 leading-tight">
            Smart DeFi Yield<br />
            <span className="bg-gradient-to-r from-[#00ff88] via-[#00d4ff] to-[#00ff88] bg-clip-text text-transparent">Intelligence</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-7 leading-relaxed">
            Find the highest real yields across 6,500+ DeFi pools.
            See true returns after gas, fees, and impermanent loss —{' '}
            <span className="text-white font-medium">before you invest.</span>
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <div className="flex flex-col items-center">
              <button onClick={handleGetStarted} className="group flex items-center gap-2 px-8 py-4 bg-[#00ff88] text-black font-bold rounded-xl hover:bg-[#00cc6a] transition-all hover:scale-105">
                Start 7-Day Pro Trial
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-[11px] text-gray-500 mt-2 text-center">
                Create your account next (takes ~30 seconds). No charge until day 7.
              </p>
            </div>
            <a href="#story" className="flex items-center gap-2 px-8 py-4 bg-gray-800/50 text-white font-medium rounded-xl hover:bg-gray-800 transition-colors border border-gray-700">
              See Use Case
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#00ff88]" /><span>No wallet connection required</span></div>
            <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-[#00ff88]" /><span>Read-only · We never touch your funds</span></div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#00ff88]" /><span>Cancel anytime</span></div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 border-y border-gray-800/50 bg-[#0f0f1a]/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1"><AnimatedNumber end={7000} suffix="+" /></div>
              <p className="text-sm text-gray-500">DeFi Pools Tracked</p>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-[#00ff88] mb-1"><AnimatedNumber end={24} suffix="/7" /></div>
              <p className="text-sm text-gray-500">Real-Time Monitoring</p>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-[#00d4ff] mb-1"><AnimatedNumber end={80} suffix="+" /></div>
              <p className="text-sm text-gray-500">Chains Supported</p>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-[#ffa502] mb-1"><AnimatedNumber end={100} prefix="$" suffix="M+" /></div>
              <p className="text-sm text-gray-500">TVL Analyzed Daily</p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full mb-6">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400 font-medium">The DeFi Trap</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Stop Chasing <span className="text-red-400">Fake APYs</span></h2>
              <div className="space-y-4 text-gray-400">
                <p className="flex items-start gap-3"><span className="text-red-400 font-bold">✗</span><span>"100% APY" pools that lose money after gas fees</span></p>
                <p className="flex items-start gap-3"><span className="text-red-400 font-bold">✗</span><span>Hidden impermanent loss that silently eats your principal</span></p>
                <p className="flex items-start gap-3"><span className="text-red-400 font-bold">✗</span><span>Unaudited protocols with rug-pull risk</span></p>
                <p className="flex items-start gap-3"><span className="text-red-400 font-bold">✗</span><span>Hours of manual research across dozens of chains</span></p>
                <p className="flex items-start gap-3"><span className="text-red-400 font-bold">✗</span><span><strong className="text-red-300">Information fragmentation</strong> — APY on one site, security data on another, IL calculators somewhere else</span></p>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00ff88]/10 border border-[#00ff88]/20 rounded-full mb-6">
                <Target className="w-4 h-4 text-[#00ff88]" />
                <span className="text-sm text-[#00ff88] font-medium">The ProfitSpot Solution</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">See <span className="text-[#00ff88]">True Returns</span> Instantly</h2>
              <div className="space-y-4 text-gray-400">
                <p className="flex items-start gap-3"><span className="text-[#00ff88] font-bold">✓</span><span><strong className="text-white">Net APY</strong> after gas, fees, and IL — calculated automatically</span></p>
                <p className="flex items-start gap-3"><span className="text-[#00ff88] font-bold">✓</span><span><strong className="text-white">Security scores</strong> from GoPlus on every pool you inspect</span></p>
                <p className="flex items-start gap-3"><span className="text-[#00ff88] font-bold">✓</span><span><strong className="text-white">Portfolio tracking</strong> — see your live LP positions & IL risk</span></p>
                <p className="flex items-start gap-3"><span className="text-[#00ff88] font-bold">✓</span><span><strong className="text-white">AI insights</strong> — our AI Data Bot analyzes any pool or position on demand</span></p>
                <p className="flex items-start gap-3"><span className="text-[#00ff88] font-bold">✓</span><span><strong className="text-white">One unified hub</strong> — ends information fragmentation. Every metric, every tool, one platform.</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-12 px-4 bg-[#0f0f1a]/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Everything You Need to <span className="text-[#00ff88]">Win in DeFi</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm">Professional-grade tools that hedge funds pay thousands for — now accessible to everyone.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FeatureCard icon={BarChart3} title="Real Yield Calculator" description="True APY after gas fees, protocol fees, and impermanent loss. Break-even in days, net return in dollars — no more nasty surprises." accent="#00ff88" />
            <FeatureCard icon={Shield} title="Security Scores" description="Every pool rated by GoPlus Security. Rug-pull risk, ownership flags, and audit status — visible before you commit a single dollar." accent="#00d4ff" />
            <FeatureCard icon={Brain} title="AI Pool Insights" description="Ask our AI Data Bot to analyze any pool on demand. Get a plain-English verdict — Strong Opportunity, Proceed with Caution, or High Risk." accent="#ffa502" badge="NEW" />
            <FeatureCard icon={Wallet} title="Portfolio Tracker" description="Paste any wallet address and instantly see all LP positions, IL risk rating, token breakdown, and AI 'Should I Exit?' recommendations." accent="#a855f7" badge="NEW" />
            <FeatureCard icon={Users} title="Whale Tracking" description="See what smart money is doing. Track Vitalik, Wintermute, and top DeFi wallets. Follow the capital, not the hype." accent="#f43f5e" />
            <FeatureCard icon={Bell} title="Smart Alerts" description="Customizable notifications when high-yield opportunities match your criteria. Filter by chain, protocol, min APY, and IL tolerance." accent="#14b8a6" />
            <FeatureCard icon={LineChart} title="What-If Simulator" description="Test 4 market scenarios before committing capital — bull, bear, sideways, and volatile. Know your outcomes in advance." accent="#00d4ff" />
            <FeatureCard icon={Globe} title="80+ Chains Covered" description="Ethereum, Arbitrum, Base, Solana, BSC, Polygon — every major chain in one dashboard. No tab-switching, no missed opportunities." accent="#627EEA" />
            <FeatureCard icon={Zap} title="Advanced Backtesting" description="Run Monte Carlo simulations over custom time windows. Search any pool by name and load its real data into the backtest engine." accent="#00ff88" badge="Elite" />
          </div>
        </div>
      </section>

      {/* Alex's Story */}
      <AlexStory onCTA={handleGetStarted} />

      {/* See It In Action */}
      <section id="how-it-works" className="py-12 px-4 bg-[#0f0f1a]/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">See It In <span className="text-[#00d4ff]">Action</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm">Every number is live. Every cost is real. This is what informed DeFi investing looks like.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">

            {/* Pool Analysis */}
            <div className="bg-[#0a0a12] border border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 transition-colors">
              <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
                  </div>
                  <span className="text-xs text-gray-500 ml-1">Pool Analysis — CARROT-USDC</span>
                </div>
                <span className="text-[10px] px-2 py-0.5 bg-[#00ff88]/10 text-[#00ff88] rounded font-medium">LIVE DATA</span>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div><p className="text-[10px] text-gray-500 uppercase tracking-wider">Protocol</p><p className="text-sm font-bold text-white">uniswap-v2 <span className="text-gray-500 font-normal">on</span> ethereum</p></div>
                  <div className="text-center"><p className="text-[10px] text-gray-500 uppercase tracking-wider">Gross APR</p><p className="text-2xl font-bold text-[#00d4ff]">199.88%</p></div>
                  <div className="text-center"><p className="text-[10px] text-gray-500 uppercase tracking-wider">Net APR</p><p className="text-2xl font-bold text-[#00ff88]">78.17%</p></div>
                </div>
                <div className="bg-[#050508] rounded-xl p-3 mb-3 border border-gray-800/50">
                  <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-3.5 h-3.5 text-[#00ff88]" /><span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Time to Profit</span></div>
                  <TimeToProfitChart />
                  <p className="text-[10px] text-center mt-1.5"><span className="text-[#ffa502] font-semibold">Yield overtakes costs at day 18</span><span className="text-gray-600"> — based on $1,000 deposit</span></p>
                </div>
                <div className="bg-[#050508] rounded-xl p-3 border border-gray-800/50">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Cost Breakdown</p>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Gas (Entry + Exit)', value: '-$0.04' },
                      { label: 'Slippage (1%)', value: '-$20.00' },
                      { label: 'Est. IL (high)', value: '-$80.00' },
                    ].map((row, i) => (
                      <div key={i} className="flex justify-between text-xs"><span className="text-gray-400">{row.label}</span><span className="text-red-400 font-medium tabular-nums">{row.value}</span></div>
                    ))}
                    <div className="flex justify-between text-xs border-t border-gray-800 pt-1.5"><span className="text-white font-semibold">Total Costs</span><span className="text-red-400 font-bold tabular-nums">-$100.04</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Yield Explorer */}
            <div className="bg-[#0a0a12] border border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 transition-colors">
              <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
                  </div>
                  <span className="text-xs text-gray-500 ml-1">Yield Explorer — Top Opportunities</span>
                </div>
                <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" /><span className="text-[10px] text-[#00ff88]">LIVE</span></div>
              </div>
              <div className="p-4 space-y-2">
                <EnhancedPoolRow symbol="ETH-USDC" protocol="Uniswap V3" chain="Arbitrum" grossApy="42.8" netApy="38.1" breakEven="9d" rating="TOP" />
                <EnhancedPoolRow symbol="stETH-ETH" protocol="Lido / Curve" chain="Ethereum" grossApy="8.4" netApy="7.9" breakEven="3d" rating="SOLID" />
                <EnhancedPoolRow symbol="ARB-ETH" protocol="Camelot" chain="Arbitrum" grossApy="61.2" netApy="44.7" breakEven="14d" rating="TOP" />
                <EnhancedPoolRow symbol="USDC-USDT" protocol="Curve" chain="Polygon" grossApy="12.1" netApy="11.6" breakEven="2d" rating="SOLID" />
                <div className="pt-2 flex items-center justify-between text-[10px] text-gray-600 border-t border-gray-800">
                  <span>6,500+ pools scanned</span>
                  <span className="text-[#00d4ff]">80+ chains covered</span>
                  <span>Updated every 5 min</span>
                </div>
              </div>
            </div>

            {/* Portfolio Tracker Preview — AI "Should I Exit?" */}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Portfolio Tracker · <span className="text-[#00d4ff]">AI Exit Analysis</span></p>
              <PortfolioPreview />
            </div>

            {/* 4-Scenario Backtesting */}
            <div className="bg-[#0a0a12] border border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 transition-colors">
              <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
                  </div>
                  <span className="text-xs text-gray-500 ml-1">Simulator Labs — 4-Scenario Backtest</span>
                </div>
                <span className="text-[10px] text-[#ffa502]">Elite</span>
              </div>
              <div className="p-4 space-y-2">
                <p className="text-[10px] text-gray-500 mb-1">$10,000 deposit · ETH-USDC · 90 days</p>
                {[
                  { label: 'Bull Market', profit: '+$1,452', alpha: '+$380 vs HODL', color: '#00ff88', verdict: 'PROFITABLE' },
                  { label: 'Stable Market', profit: '+$1,107', alpha: '+$89 vs HODL', color: '#00d4ff', verdict: 'SOLID' },
                  { label: 'Bear Market', profit: '+$666', alpha: '-$210 vs HODL', color: '#ffa502', verdict: 'MARGINAL' },
                  { label: 'Sideways Grind', profit: '+$963', alpha: '+$141 vs HODL', color: '#00ff88', verdict: 'PROFITABLE' },
                ].map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-[#050508] rounded-lg border border-gray-800/50">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                      <span className="text-xs text-gray-300">{s.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-right">
                      <span className="text-[10px] text-gray-500 hidden sm:block">{s.alpha}</span>
                      <span className="text-xs font-bold" style={{ color: s.color }}>{s.profit}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ color: s.color, backgroundColor: `${s.color}15` }}>{s.verdict}</span>
                    </div>
                  </div>
                ))}
                <p className="text-[10px] text-center text-gray-600 pt-1">Powered by ProfitSpot AI Engine</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Trust */}
      <section className="py-10 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Built for <span className="text-[#00d4ff]">Serious</span> DeFi Investors</h2>
          <p className="text-gray-400 text-sm max-w-2xl mx-auto mb-8">Real-time data from DeFiLlama, GoPlus Security, and Moralis — aggregated across 80+ chains so you never miss an opportunity.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#0a0a12] border border-gray-800 rounded-xl p-5 text-center">
              <p className="text-2xl font-bold text-[#00ff88]">6,500+</p>
              <p className="text-xs text-gray-500 mt-1">Yield Pools Tracked</p>
            </div>
            <div className="bg-[#0a0a12] border border-gray-800 rounded-xl p-5 text-center">
              <p className="text-2xl font-bold text-[#00d4ff]">80+</p>
              <p className="text-xs text-gray-500 mt-1">Chains Covered</p>
            </div>
            <div className="bg-[#0a0a12] border border-gray-800 rounded-xl p-5 text-center">
              <p className="text-2xl font-bold text-[#ffa502]">Real-Time</p>
              <p className="text-xs text-gray-500 mt-1">APY & Security Data</p>
            </div>
            <div className="bg-[#0a0a12] border border-gray-800 rounded-xl p-5 text-center">
              <p className="text-2xl font-bold text-[#a855f7]">AI-Powered</p>
              <p className="text-xs text-gray-500 mt-1">Pool Analysis & Alerts</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-12 px-4 bg-[#0f0f1a]/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Simple, <span className="text-[#ffa502]">Transparent</span> Pricing</h2>
            <p className="text-gray-400 text-sm max-w-2xl mx-auto">Start free, upgrade when you're ready. Pro includes a 7-day free trial.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <PricingTier
              name="Free"
              price="0"
              period=""
              accent="#9ca3af"
              features={['25 yield pools', 'Risk grade badges (A/B/C/D/F)', 'Security score badges', 'Trending Chains leaderboard', 'Bitcoin DeFi spotlight', 'Basic IL Calculator', 'Simulator preview']}
              cta="Get Started Free"
              onCTA={handleGetStarted}
            />
            <PricingTier
              name="Pro"
              price="29"
              period="mo"
              accent="#00d4ff"
              popular
              features={['500+ yield pools', 'Full risk grade details & filter', 'Full security detail panel', 'Gas Time-to-Profit (TTP)', 'Portfolio Analyzer + risk grades', '3 pool APY/TVL alerts', 'Full Simulator + Time-to-Profit charts', '7-day free trial']}
              cta="Start 7-Day Free Trial"
              onCTA={handleGetStarted}
              finePrint="7-day free trial, then $29/mo. Annual: $19/mo ($228/yr). Cancel anytime."
            />
            <PricingTier
              name="Elite"
              price="79"
              period="mo"
              accent="#ffa502"
              features={['Unlimited pools', 'Everything in Pro', 'Rate My Portfolio optimizer', 'Whale Tracker (smart money)', 'AI Pool Insights', 'Forecast Dial (Monte Carlo + TTP)', 'Unlimited alerts + Telegram', 'CSV export + priority support']}
              cta="Go Elite"
              onCTA={handleGetStarted}
              finePrint="Annual: $59/mo ($708/yr)"
            />
          </div>
          <div className="text-center mt-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00ff88]/10 border border-[#00ff88]/20 rounded-full">
              <Shield className="w-4 h-4 text-[#00ff88]" />
              <span className="text-sm text-[#00ff88]">Free plan available · Pro: 7-day trial, then $29/mo · Secure payments via Stripe</span>
            </div>
          </div>
        </div>
      </section>


      {/* Founder */}
      <section id="founder" className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3">Built by a <span className="text-[#00ff88]">Founder</span> Who Gets It</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">No VC funding. No 10-person dev team. Just relentless focus on giving DeFi investors the data they actually need.</p>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Founder Card */}
            <div className="bg-[#0f0f1a]/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-gray-700 transition-all">
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={`${process.env.PUBLIC_URL}/founder.jpg`}
                  alt="Matthew Ball — Founder of ProfitSpot"
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#00ff88]/30"
                />
                <div>
                  <h3 className="text-xl font-bold text-white">Matthew Ball</h3>
                  <p className="text-sm text-gray-400">Founder & Builder</p>
                </div>
              </div>
              <div className="space-y-4 text-gray-300 text-[15px] leading-relaxed">
                <p>I build tools that solve real problems — and my latest one analyzes <span className="text-white font-semibold">$133B in DeFi liquidity</span>.</p>
                <p>After watching close friends get burned by misleading yield farming data, I built the tool that should've existed: one dashboard covering <span className="text-white font-semibold">6,500+ pools across 86 chains</span>, with net profit calculations, security scores, AI-powered analysis, and whale tracking.</p>
                <p>I built it with AI agents as my engineering team. No VC funding. No 10-person dev team. Just one person leveraging AI to ship at <span className="text-[#00ff88] font-semibold">10x speed</span>.</p>
              </div>
              <div className="flex items-center gap-5 mt-6 pt-6 border-t border-gray-800">
                <a href="https://instagram.com/mattyball" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#00ff88] transition-colors">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  @mattyball
                </a>
                <a href="https://instagram.com/profitspot.live" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#00ff88] transition-colors">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  @profitspot.live
                </a>
                <a href="https://twitter.com/profitspotlive" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#00ff88] transition-colors">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  @profitspotlive
                </a>
              </div>
            </div>

            {/* Value Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-[#0f0f1a]/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all group">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-[#00ff88]/10 group-hover:scale-110 transition-transform">
                  <Rocket className="w-5 h-5 text-[#00ff88]" />
                </div>
                <h4 className="text-white font-bold mb-2">Bootstrapped & Independent</h4>
                <p className="text-sm text-gray-400 leading-relaxed">No investors to please. Every decision optimizes for you, the user — not for growth metrics.</p>
              </div>
              <div className="bg-[#0f0f1a]/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all group">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-[#00d4ff]/10 group-hover:scale-110 transition-transform">
                  <Shield className="w-5 h-5 text-[#00d4ff]" />
                </div>
                <h4 className="text-white font-bold mb-2">Non-Custodial & Transparent</h4>
                <p className="text-sm text-gray-400 leading-relaxed">We never touch your funds. Read-only analytics, open pricing, no hidden fees — ever.</p>
              </div>
              <div className="bg-[#0f0f1a]/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all group">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-[#ff6b6b]/10 group-hover:scale-110 transition-transform">
                  <Heart className="w-5 h-5 text-[#ff6b6b]" />
                </div>
                <h4 className="text-white font-bold mb-2">Built With Empathy</h4>
                <p className="text-sm text-gray-400 leading-relaxed">As a solo founder, I know every dollar matters. That's why ProfitSpot starts at $0 — upgrade when you're ready.</p>
              </div>
              <div className="bg-[#0f0f1a]/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all group">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-[#ffa502]/10 group-hover:scale-110 transition-transform">
                  <Zap className="w-5 h-5 text-[#ffa502]" />
                </div>
                <h4 className="text-white font-bold mb-2">AI-Powered Intelligence</h4>
                <p className="text-sm text-gray-400 leading-relaxed">Leveraging cutting-edge AI to deliver institutional-grade DeFi analytics at a fraction of the cost.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a12] to-[#0f0f1a]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">Ready to Find <span className="text-[#00ff88]">Real Yields</span>?</h2>
          <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">Join DeFi investors who've stopped chasing fake APYs and started making informed, data-backed decisions.</p>
          <button onClick={handleGetStarted} className="group inline-flex items-center gap-2 px-10 py-5 bg-[#00ff88] text-black text-lg font-bold rounded-xl hover:bg-[#00cc6a] transition-all hover:scale-105">
            Start Your 7-Day Pro Trial
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-sm text-gray-600 mt-3">
            Create your account next (takes ~30 seconds). No charge until day 7.
          </p>
          <p className="text-sm text-gray-600 mt-4">After 7 days, Pro continues at $29/mo. Cancel anytime. Annual plans available.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00ff88] to-[#00d4ff] flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-black" />
                </div>
                <span className="text-lg font-bold">PROFIT<span className="text-[#00ff88]">SPOT</span></span>
              </div>
              <p className="text-sm text-gray-500">Non-custodial DeFi yield intelligence. Your keys, your crypto.</p>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://betalist.com/startups/profitspot?utm_campaign=badge-profitspot&utm_medium=badge&utm_source=badge-featured"
                className="inline-block mt-4 opacity-90 hover:opacity-100 transition-opacity"
              >
                <img
                  alt="ProfitSpot - Featured on BetaList"
                  width="156"
                  height="54"
                  style={{ width: '156px', height: '54px' }}
                  src="/betalist-badge.svg"
                />
              </a>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#story" className="hover:text-white transition-colors">Real Story</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="/advisors" className="hover:text-[#00ff88] transition-colors font-medium">For Advisors</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="/terms" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/disclaimer" className="hover:text-white transition-colors">Risk Disclaimer</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="mailto:support@profitspot.live" className="hover:text-white transition-colors">support@profitspot.live</a></li>
                <li><a href="https://contact-forms-6a9d114d.viktor.space/profitspot" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Contact Form</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-600">© 2026 ProfitSpot — a product of MGB Inc. Non-custodial DeFi intelligence.</p>
            <p className="text-xs text-gray-700">Not financial advice. Cryptocurrency investments carry significant risk. DYOR. Past performance is not indicative of future results.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
